import { Set as ImmutableSet } from 'immutable';
import { useCallback, useMemo, useState, useEffect } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Text } from 'foundations-components/components/Text';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TableActionsRow } from 'foundations-components/transitional/components/Table';
import TransferOwnerModal from './TransferOwnerModal';
import { Link } from 'foundations-components/transitional/components/Link';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoWrapTableHeader = styled.th.withConfig({
  displayName: "MeetingsTableHeader__NoWrapTableHeader"
})(["white-space:nowrap;"]);
const BUSINESS_UNIT_HEADER = {
  className: 'th-link-business-unit',
  contentKey: 'businessUnit'
};
const HEADER_CELLS = shouldRenderBusinessUnit => [{
  className: 'th-link-name',
  contentKey: 'meeting'
}, {
  className: 'th-link-owner',
  contentKey: 'owner'
}, {
  className: 'th-link-type',
  contentKey: 'type'
}, {
  className: 'th-link-durations',
  contentKey: 'durations'
}, ...(shouldRenderBusinessUnit ? [BUSINESS_UNIT_HEADER] : []), {
  className: 'th-link-views',
  contentKey: 'views'
}, {
  className: 'th-link-meetings-booked',
  contentKey: 'meetingsBooked'
}, {
  className: 'th-link-conversion-rate',
  contentKey: 'conversionRate'
}];
const renderHeaderText = (contentKey, text) => {
  const shouldRenderTooltip = contentKey === 'views';
  return shouldRenderTooltip ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [text, /*#__PURE__*/_jsx(HelpIcon, {
      alignWithText: true,
      marginInlineStart: "100",
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "adminHeader.table.viewsTooltip.title"
      })
    })]
  }) : text;
};
const renderHeader = data => {
  const {
    className,
    contentKey
  } = data;
  const text = contentKey ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: `adminHeader.table.headers.${contentKey}`
  }) : contentKey;
  const Element = className === 'th-link-views' ? NoWrapTableHeader : 'th';
  return /*#__PURE__*/_jsx(Element, {
    children: renderHeaderText(contentKey, text)
  }, contentKey);
};
const MeetingsTableHeader = ({
  onSelectRows = emptyFunction,
  meetingsLinks = [],
  selectedRows = ImmutableSet(),
  canEditAllLinks,
  shouldOpenTransferModal = false,
  shouldRenderBusinessUnit = false,
  onTransferSuccess = emptyFunction
}) => {
  const [transferOwnerModalOpen, setTransferOwnerModalOpen] = useState(false);
  const handleToggleTransfer = useCallback(() => setTransferOwnerModalOpen(open => !open), [setTransferOwnerModalOpen]);
  const handleTransferSuccess = useCallback(() => {
    onTransferSuccess();
    onSelectRows(ImmutableSet());
  }, [onSelectRows, onTransferSuccess]);
  useEffect(() => {
    if (shouldOpenTransferModal) {
      setTransferOwnerModalOpen(true);
    }
  }, [shouldOpenTransferModal]);
  const numLinks = meetingsLinks && meetingsLinks.length;
  const selectedRowCount = selectedRows && selectedRows.size;
  const defaultLinkIsSelected = useMemo(() => {
    const selectedDefaultLinkIds = selectedRows && selectedRows.filter(row => !!meetingsLinks.find(link => link.id === row && link.isDefaultLink));
    return selectedDefaultLinkIds && selectedDefaultLinkIds.size;
  }, [meetingsLinks, selectedRows]);
  const selectedRowUserId = useMemo(() => {
    if (!selectedRows || !selectedRows.size || !meetingsLinks) {
      return null;
    }
    const linksToTransfer = meetingsLinks.filter(link => selectedRows.has(link.id));

    // Don't allow mixed-owner transfers. See https://hubspot.slack.com/archives/CT1NEGM5F/p1598890181006400
    const firstLinkUserId = linksToTransfer.length > 0 ? linksToTransfer[0].userId : null;
    if (firstLinkUserId && linksToTransfer.some(link => link.userId !== firstLinkUserId)) {
      return null;
    }
    return firstLinkUserId;
  }, [selectedRows, meetingsLinks]);
  const bulkSelect = /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Checkbox, {
      "aria-label": I18n.text('adminHeader.table.bulkActions.headerLabel'),
      checked: selectedRowCount === numLinks,
      indeterminate: selectedRowCount > 0,
      onChange: () => {
        const selectAll = selectedRowCount === 0;
        const newSelectedRows = meetingsLinks.reduce((linkSet, link) => {
          if (link.id !== 'new') {
            return linkSet.add(link.id);
          }
          return linkSet;
        }, ImmutableSet());
        onSelectRows(selectAll ? newSelectedRows : ImmutableSet());
      }
    })
  }, "th-link-bulk-actions");
  const transferDisabledReason = useMemo(() => {
    if (selectedRowUserId === null) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.table.bulkActions.transferSingleOwnerDisabled"
      });
    }
    if (defaultLinkIsSelected) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.table.bulkActions.transferDefaultDisabled"
      });
    }
    return null;
  }, [selectedRowUserId, defaultLinkIsSelected]);
  const renderTransferModal = () => {
    if (!selectedRowUserId) {
      return null;
    }
    return /*#__PURE__*/_jsx(TransferOwnerModal, {
      meetingsLinks: meetingsLinks,
      onSuccess: handleTransferSuccess,
      onClose: handleToggleTransfer,
      selectedRows: selectedRows,
      fromHubSpotUserId: selectedRowUserId,
      isEditView: false
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("thead", {
      children: /*#__PURE__*/_jsxs(TableActionsRow, {
        open: selectedRowCount > 0,
        openAtColumnIndex: 1,
        actions: /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "adminHeader.table.bulkActions.numSelected",
              options: {
                selectedRowCount
              }
            })
          }), /*#__PURE__*/_jsx(List, {
            inline: true,
            children: /*#__PURE__*/_jsx(ListItem, {
              marginInlineStart: "300",
              children: /*#__PURE__*/_jsx(Tooltip, {
                title: transferDisabledReason,
                placement: "bottom right",
                children: /*#__PURE__*/_jsxs(Link, {
                  onClick: handleToggleTransfer,
                  disabled: !!transferDisabledReason,
                  "data-selenium-test": "transfer-button",
                  children: [/*#__PURE__*/_jsx(ContactsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "adminHeader.table.bulkActions.transfer"
                  })]
                })
              })
            })
          })]
        }),
        children: [canEditAllLinks && bulkSelect, HEADER_CELLS(shouldRenderBusinessUnit).map(heading => {
          return renderHeader(heading);
        })]
      })
    }), transferOwnerModalOpen && renderTransferModal()]
  });
};
export default MeetingsTableHeader;